import { Loader2 } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AuditEntityComparison,
  AuditMergeExplanation,
  AuditPropertySnapshot,
} from '@/components/entity-resolution/AuditMergeExplanation'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useAuditRecord } from '@/features/entity-resolution/hooks/useAuditLog'

/** Uses the same evidence as the audit log, in a modal that also works over an entity sheet. */
export function HistoryMergeAudit({
  caseId,
  auditId,
  onSelect,
}: {
  caseId: string
  auditId: string | null
  onSelect: (id: string | null) => void
}) {
  const returnFocus = useRef<HTMLElement | null>(null)
  const { t } = useTranslation()
  return (
    <Sheet open={auditId !== null} onOpenChange={open => !open && onSelect(null)}>
      <SheetContent
        className="sm:max-w-3xl"
        aria-describedby={undefined}
        onOpenAutoFocus={() => {
          returnFocus.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null
        }}
        onCloseAutoFocus={event => {
          event.preventDefault()
          returnFocus.current?.focus()
        }}
      >
        <SheetHeader>
          <SheetTitle>{t('cases.investigate.entityResolution.audit.drawer.title')}</SheetTitle>
        </SheetHeader>
        <AuditEvidence caseId={caseId} auditId={auditId} onSelect={onSelect} />
      </SheetContent>
    </Sheet>
  )
}

function AuditEvidence({
  caseId,
  auditId,
  onSelect,
}: {
  caseId: string
  auditId: string | null
  onSelect: (id: string) => void
}) {
  const { t } = useTranslation()
  const query = useAuditRecord(caseId, auditId)
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
      {query.isError ? (
        <div role="alert" className="space-y-2 text-sm">
          <p>{t('cases.investigate.entityResolution.audit.drawer.loadError')}</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            {t('investigations.entityDetail.history.retry')}
          </Button>
        </div>
      ) : query.data ? (
        <>
          <AuditEntityComparison record={query.data} onOpenAudit={onSelect} />
          <AuditMergeExplanation record={query.data} />
          <AuditPropertySnapshot record={query.data} />
        </>
      ) : (
        <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 aria-hidden className="size-4 animate-spin" />
          {t('investigations.entityDetail.loading')}
        </div>
      )}
    </div>
  )
}
